import { config } from '../config'

/**
 * Validates if a file is a supported video format
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, error: string }
 */
export function validateVideoFile(file) {
  if (!file) {
    return { isValid: false, error: 'No file provided' }
  }

  // Check if it's a video file
  if (!file.type.startsWith('video/')) {
    return { isValid: false, error: 'File must be a video' }
  }

  // Check file size
  if (file.size > config.upload.maxFileSize) {
    const maxSizeMB = config.upload.maxFileSize / (1024 * 1024)
    return { 
      isValid: false, 
      error: `File size must be less than ${maxSizeMB}MB` 
    }
  }

  // Check if format is supported
  if (!config.upload.acceptedFormats.includes(file.type)) {
    return { 
      isValid: false, 
      error: `Unsupported format. Supported formats: ${config.upload.acceptedExtensions.join(', ')}` 
    }
  }

  return { isValid: true, error: null }
}

/**
 * Converts a file to base64 data URL
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 data URL
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

/**
 * Formats file size to human-readable string
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Gets video duration from a file
 * @param {File} file - Video file
 * @returns {Promise<number>} - Duration in seconds
 */
export function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src)
      resolve(video.duration)
    }
    
    video.onerror = () => {
      reject(new Error('Failed to load video metadata'))
    }
    
    video.src = URL.createObjectURL(file)
  })
}

/**
 * Formats duration in seconds to MM:SS format
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration
 */
export function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Extracts keyframes from a video file as base64 images
 * Distributes frames evenly across the specified trim range (or entire video)
 * @param {File} file - Video file
 * @param {Object} options - Extraction options
 * @param {number} options.maxFrames - Maximum number of frames to extract (default: 10)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {number} options.trimStart - Start time in seconds (default: 0)
 * @param {number} options.trimEnd - End time in seconds (default: video duration)
 * @param {boolean} options.enablePoseOverlay - Whether to overlay pose skeleton on frames
 * @param {Object} options.poseExtractor - PoseExtractor instance for pose detection
 * @returns {Promise<Object>} - { frames: Array<string>, poseFrames: Array<Object>|null }
 */
export async function extractKeyframes(file, options = {}) {
  const {
    maxFrames = 10,
    quality = 0.8,
    trimStart = 0,
    trimEnd = null,  // null means use full video duration
    enablePoseOverlay = false,
    poseExtractor = null
  } = options

  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const frames = []
    const poseFrames = [] // Store pose data for each frame
    
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true
    
    video.onloadedmetadata = async () => {
      const fullDuration = video.duration
      
      // Use trim range or default to full video
      const startTime = Math.max(0, trimStart)
      const endTime = trimEnd !== null ? Math.min(trimEnd, fullDuration) : fullDuration
      const trimmedDuration = endTime - startTime
      
      // Divide the trimmed range into equal segments
      // Extract one frame from the center of each segment for even distribution
      const segmentDuration = trimmedDuration / maxFrames
      
      console.log(`Video duration: ${fullDuration.toFixed(2)}s`)
      console.log(`Trim range: ${startTime.toFixed(2)}s - ${endTime.toFixed(2)}s (${trimmedDuration.toFixed(2)}s)`)
      console.log(`Extracting ${maxFrames} frames, one every ${segmentDuration.toFixed(2)}s`)
      console.log(`Pose overlay: ${enablePoseOverlay && poseExtractor ? 'enabled' : 'disabled'}`)
      
      // Set canvas size (scale down for efficiency while maintaining aspect ratio)
      const maxWidth = 1024
      const scale = Math.min(1, maxWidth / video.videoWidth)
      canvas.width = video.videoWidth * scale
      canvas.height = video.videoHeight * scale
      
      console.log(`Canvas size: ${canvas.width}x${canvas.height}`)
      
      // Extract frames evenly distributed across the trimmed range
      for (let i = 0; i < maxFrames; i++) {
        // Extract from the middle of each segment for better representation
        // Time is relative to trimmed range, starting from startTime
        const time = startTime + (i + 0.5) * segmentDuration
        const frameNumber = i + 1
        const timestampMs = Math.floor(time * 1000)
        
        try {
          await seekToTime(video, time)
          
          // Draw current frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          
          // Pose detection and overlay (if enabled)
          if (enablePoseOverlay && poseExtractor) {
            try {
              // Detect pose on the video element (not canvas) for better accuracy
              const poseResult = poseExtractor.detectForFrame(video, timestampMs)
              
              if (poseResult?.landmarks?.length > 0) {
                // Draw skeleton overlay on canvas
                poseExtractor.drawPoseOnContext(ctx, poseResult, canvas.width, canvas.height)
                
                // Store pose frame data
                const poseFrame = poseExtractor.convertResultToFrame(poseResult, timestampMs)
                if (poseFrame) {
                  poseFrames.push(poseFrame)
                }
                
                console.log(`Frame ${frameNumber}: Pose detected with ${poseResult.landmarks[0].length} landmarks`)
              } else {
                console.log(`Frame ${frameNumber}: No pose detected`)
              }
            } catch (poseError) {
              console.warn(`Pose detection failed for frame ${frameNumber}:`, poseError)
            }
          }
          
          // Annotate frame with frame number and timestamp in top left
          // Black text with white outline for visibility on any background
          const annotationText = `Frame ${frameNumber} | ${time.toFixed(2)}s`
          const fontSize = Math.max(16, Math.floor(canvas.width / 40)) // Responsive font size
          ctx.font = `bold ${fontSize}px Arial, sans-serif`
          ctx.textBaseline = 'top'
          ctx.textAlign = 'left'
          
          const padding = Math.floor(fontSize * 0.5)
          const x = padding
          const y = padding
          
          // Draw white outline (stroke) first
          ctx.strokeStyle = 'white'
          ctx.lineWidth = Math.max(3, fontSize / 8)
          ctx.lineJoin = 'round'
          ctx.strokeText(annotationText, x, y)
          
          // Draw black fill on top
          ctx.fillStyle = 'black'
          ctx.fillText(annotationText, x, y)
          
          // Convert to base64 JPEG
          const frameData = canvas.toDataURL('image/jpeg', quality)
          frames.push(frameData)
          
          console.log(`Extracted frame ${frameNumber}/${maxFrames} at ${time.toFixed(2)}s`)
        } catch (error) {
          console.warn(`Failed to extract frame ${frameNumber} at ${time.toFixed(2)}s:`, error)
        }
      }
      
      // Cleanup
      URL.revokeObjectURL(video.src)
      
      if (frames.length === 0) {
        reject(new Error('Failed to extract any frames from video'))
      } else {
        console.log(`Successfully extracted ${frames.length} frames`)
        if (enablePoseOverlay) {
          console.log(`Extracted ${poseFrames.length} pose frames`)
        }
        // Return both frames and pose data
        resolve({
          frames,
          poseFrames: enablePoseOverlay && poseFrames.length > 0 ? poseFrames : null
        })
      }
    }
    
    video.onerror = () => {
      URL.revokeObjectURL(video.src)
      reject(new Error('Failed to load video for frame extraction'))
    }
    
    video.src = URL.createObjectURL(file)
  })
}

/**
 * Helper function to seek video to a specific time
 * @param {HTMLVideoElement} video - Video element
 * @param {number} time - Time in seconds
 * @returns {Promise<void>}
 */
function seekToTime(video, time) {
  return new Promise((resolve, reject) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('error', onError)
      resolve()
    }
    
    const onError = (error) => {
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('error', onError)
      reject(error)
    }
    
    video.addEventListener('seeked', onSeeked)
    video.addEventListener('error', onError)
    video.currentTime = time
  })
}

